import React, { useState } from 'react';
import { Clock, ArrowLeft, Shield, ChevronDown, ChevronUp, Mail } from 'lucide-react';

const SECTIONS = [
  {
    id: 'overview',
    title: 'Überblick der Verarbeitung',
    content: (
      <div className="space-y-4 text-slate-400 font-medium leading-relaxed">
        <p>
          Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren
          personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene
          Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
        </p>
        <p>
          Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem
          Text aufgeführten Datenschutzerklärung.
        </p>
      </div>
    ),
  },
  {
    id: 'data-collection',
    title: 'Datenerfassung auf dieser Website',
    content: (
      <div className="space-y-4 text-slate-400 font-medium leading-relaxed">
        <div>
          <p className="text-white font-bold mb-2">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</p>
          <p>
            Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen
            Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
          </p>
        </div>
        <div>
          <p className="text-white font-bold mb-2">Wie erfassen wir Ihre Daten?</p>
          <p>
            Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei
            kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
          </p>
          <p className="mt-3">
            Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website
            durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z. B.
            Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung
            dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.
          </p>
        </div>
        <div>
          <p className="text-white font-bold mb-2">Wofür nutzen wir Ihre Daten?</p>
          <p>
            Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu
            gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
          </p>
        </div>
        <div>
          <p className="text-white font-bold mb-2">Welche Rechte haben Sie bezüglich Ihrer Daten?</p>
          <p>
            Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und
            Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem
            ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine
            Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung
            jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten
            Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu
            verlangen.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'hosting',
    title: 'Hosting',
    content: (
      <div className="space-y-4 text-slate-400 font-medium leading-relaxed">
        <p>
          Wir hosten die Inhalte unserer Website bei Google Firebase (Firebase Hosting),
          einem Dienst der Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA.
        </p>
        <p>
          Details entnehmen Sie der Datenschutzerklärung von Google:{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-rose-400 hover:text-rose-300 transition-colors"
          >
            https://policies.google.com/privacy
          </a>
          .
        </p>
        <p>
          Die Verwendung von Firebase Hosting erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f
          DSGVO. Wir haben ein berechtigtes Interesse an einer möglichst zuverlässigen
          Darstellung unserer Website. Sofern eine entsprechende Einwilligung abgefragt wurde,
          erfolgt die Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO.
        </p>
      </div>
    ),
  },
  {
    id: 'authentication',
    title: 'Authentifizierung & Nutzerdaten',
    content: (
      <div className="space-y-4 text-slate-400 font-medium leading-relaxed">
        <p>
          Presently verwendet Google Firebase Authentication zur Nutzeranmeldung. Dabei werden
          folgende Daten verarbeitet:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>E-Mail-Adresse des Nutzers</li>
          <li>Anzeigename und Profilbild (sofern über Google-Konto bereitgestellt)</li>
          <li>Eindeutige Nutzer-ID (UID)</li>
          <li>Zeitstempel der letzten Anmeldung</li>
        </ul>
        <p>
          Diese Daten werden ausschließlich zum Zweck der Authentifizierung und Personalisierung
          des Dienstleistungsangebots verarbeitet (Art. 6 Abs. 1 lit. b DSGVO — Vertragserfüllung).
        </p>
        <p>
          Sitzungsdaten (Timer-States, Raumzuweisungen) werden in der Firebase Realtime Database
          sowie Firestore gespeichert und nach Sitzungsende archiviert. Anonyme Display-Sessions
          speichern keine personenbezogenen Daten.
        </p>
      </div>
    ),
  },
  {
    id: 'rights',
    title: 'Ihre Rechte (DSGVO)',
    content: (
      <div className="space-y-4 text-slate-400 font-medium leading-relaxed">
        <p>Sie haben gemäß DSGVO folgende Rechte gegenüber uns:</p>
        <div className="space-y-3">
          {[
            ['Auskunft (Art. 15 DSGVO)', 'Sie können Auskunft über die von uns gespeicherten personenbezogenen Daten verlangen.'],
            ['Berichtigung (Art. 16 DSGVO)', 'Sie können die Berichtigung unrichtiger Daten verlangen.'],
            ['Löschung (Art. 17 DSGVO)', 'Sie können die Löschung Ihrer Daten verlangen, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.'],
            ['Einschränkung (Art. 18 DSGVO)', 'Sie können die Einschränkung der Verarbeitung Ihrer Daten verlangen.'],
            ['Datenübertragbarkeit (Art. 20 DSGVO)', 'Sie können Ihre Daten in einem maschinenlesbaren Format erhalten.'],
            ['Widerspruch (Art. 21 DSGVO)', 'Sie können der Verarbeitung Ihrer Daten auf Basis berechtigter Interessen widersprechen.'],
            ['Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO)', 'Erteilte Einwilligungen können Sie jederzeit widerrufen.'],
            ['Beschwerderecht (Art. 77 DSGVO)', 'Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren.'],
          ].map(([title, desc]) => (
            <div key={title} className="border-l-2 border-rose-800 pl-4">
              <p className="text-white font-bold text-sm">{title}</p>
              <p className="text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'contact-dpo',
    title: 'Kontakt & Datenschutzanfragen',
    content: (
      <div className="space-y-4 text-slate-400 font-medium leading-relaxed">
        <p>
          Bei Fragen zum Datenschutz oder zur Geltendmachung Ihrer Rechte wenden Sie sich bitte an:
        </p>
        <div className="flex items-center space-x-3 bg-slate-800 rounded-2xl p-4">
          <Mail className="w-5 h-5 text-rose-500 flex-shrink-0" />
          <a
            href="mailto:samuelrobingera@gmail.com"
            className="text-rose-400 hover:text-rose-300 font-bold transition-colors"
          >
            samuelrobingera@gmail.com
          </a>
        </div>
        <p>
          Wir werden Ihre Anfrage schnellstmöglich, spätestens innerhalb von 30 Tagen,
          beantworten (Art. 12 Abs. 3 DSGVO).
        </p>
      </div>
    ),
  },
];

const LegalNotice = ({ onBack }) => {
  const [openSection, setOpenSection] = useState('overview');

  const toggle = (id) => setOpenSection(prev => prev === id ? null : id);

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans">
      {/* Navigation */}
      <nav className="fixed w-full z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="bg-rose-600 p-2 rounded-xl">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-black tracking-tighter text-white uppercase">PRESENTLY</span>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-bold uppercase tracking-widest">Back</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">

        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center space-x-2 bg-slate-800 border border-slate-700 px-4 py-2 rounded-full mb-6">
            <Shield className="w-3.5 h-3.5 text-rose-500" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Datenschutz · DSGVO</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter text-white mb-4">
            Datenschutzerklärung
          </h1>
          <p className="text-slate-400 font-medium">
            Legal Notice &amp; Privacy Policy gemäß DSGVO / GDPR
          </p>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-4">
          {SECTIONS.map(({ id, title, content }) => {
            const isOpen = openSection === id;
            return (
              <div
                key={id}
                className={`bg-slate-900 border rounded-[28px] transition-all duration-300 overflow-hidden
                  ${isOpen ? 'border-rose-800 shadow-lg shadow-rose-900/10' : 'border-slate-800'}`}
              >
                <button
                  onClick={() => toggle(id)}
                  className="w-full flex items-center justify-between px-8 py-6 text-left group"
                >
                  <span className={`font-black text-sm uppercase tracking-widest transition-colors
                    ${isOpen ? 'text-rose-400' : 'text-slate-300 group-hover:text-white'}`}>
                    {title}
                  </span>
                  {isOpen
                    ? <ChevronUp className="w-4 h-4 text-rose-500 flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-slate-500 group-hover:text-slate-300 flex-shrink-0" />
                  }
                </button>
                {isOpen && (
                  <div className="px-8 pb-8 border-t border-slate-800 pt-6">
                    {content}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Last Updated */}
        <div className="pt-8 border-t border-slate-800 mt-8">
          <p className="text-slate-600 text-xs font-bold uppercase tracking-widest">
            Stand: Juni 2026 · Datenschutzerklärung gemäß Art. 13 DSGVO
          </p>
        </div>

      </div>
    </div>
  );
};

export default LegalNotice;
